/**
 * The audio engine seam - and the one piece of this feature that is a stub.
 *
 * ---------------------------------------------------------------------------
 * READ THIS BEFORE ASSUMING VOICE IS UNFINISHED
 * ---------------------------------------------------------------------------
 * Everything else in `src/voice` is real: the state machine, the polling, the
 * eligibility checks, the push handling, the two overlays, the permission
 * prompt. All of it talks to a live backend and all of it is exercised on a
 * real call.
 *
 * What does not exist yet is the client SDK that carries the audio. The
 * vendor's integration guide ends with "ask us for the Synquic Voice client
 * SDK", and until somebody does, there is no package to import. This file is
 * the shape of the hole, so that:
 *
 *   1. nothing above it imports a package that is not installed,
 *   2. the day the SDK lands, one `require` starts working and no other file
 *      in the app changes,
 *   3. the UI can say so honestly instead of drawing a call that is silent.
 *
 * That third point is the important one. A stub that reports `connected` and
 * plays nothing is far worse than one that refuses: a customer would sit
 * there saying "hello" into a dead line while the screen told them everything
 * was fine. `stubTransport` therefore fails on purpose, loudly, and the call
 * UI renders "Voice engine not installed" rather than a call in progress.
 */

/**
 * Where the audio connection is, as the engine sees it.
 *
 * Deliberately NOT where the *call* is. The call's state belongs to the server
 * - a call is answered because the callee hit accept, not because a socket
 * opened - and conflating the two is how an app ends up showing "connected"
 * for a call nobody picked up. The provider keeps them apart and only ever
 * uses this to answer "is the audio actually flowing".
 */
import { resolveVendorTransport } from './sdk-adapter';

export type VoiceTransportState =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'failed'
  | 'closed';

/**
 * Something went wrong that did NOT end the call.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS NOT A `VoiceTransportState`
 * ---------------------------------------------------------------------------
 * The states above are a lifecycle: exactly one is true, and the next one
 * replaces it. A microphone that failed to publish is neither - the call is
 * genuinely `connected`, audio is genuinely flowing one way, and there is no
 * moment at which the problem stops being true. Squeezing it into the
 * lifecycle would mean either lying about the connection or inventing a state
 * nothing can leave.
 *
 * `microphone-unavailable` - this side can hear and cannot be heard. On
 * Android this is close to unreachable, because `ensureMicrophone` asks before
 * the call is placed and refuses to place one without a grant. On iOS it is
 * the ordinary consequence of tapping "Don't Allow" on the system prompt,
 * which fires on first capture - by which time the call is already up.
 */
export type VoiceTransportProblem = 'microphone-unavailable';

export interface VoiceTransport {
  /**
   * Whether there is a real engine behind this.
   *
   * False on the stub. Anything that draws a call reads this first and shows
   * the missing-engine state rather than the call controls.
   */
  readonly isAvailable: boolean;

  /**
   * Joins the room for one call.
   *
   * The token is per-call and short-lived. On a reconnect the caller is
   * expected to fetch a fresh one (`POST .../calls/:id/token`) rather than
   * replay this one.
   */
  connect(input: {
    token: string;
    voiceServerUrl: string;
    callId: string;
  }): Promise<void>;

  /** Leaves the room. Safe to call when not connected. */
  disconnect(): Promise<void>;

  setMuted(muted: boolean): Promise<void>;

  /** Routes to the loudspeaker rather than the earpiece. */
  setSpeakerphone(on: boolean): Promise<void>;

  /**
   * Watches the connection. Returns its own unsubscribe.
   *
   * `detail` is for the log and for the missing-engine copy, never for a
   * customer to read as-is.
   */
  subscribe(
    listener: (state: VoiceTransportState, detail?: string) => void,
  ): () => void;

  /**
   * Watches for problems that leave the call standing. Returns its own
   * unsubscribe.
   *
   * Separate from `subscribe` because these do not replace the state and are
   * not replayed: a subscriber that arrives after the microphone failed has
   * missed it, which is correct - it is reporting an event, not a condition to
   * be caught up on.
   */
  subscribeProblems(
    listener: (problem: VoiceTransportProblem) => void,
  ): () => void;
}

/**
 * A transport that cannot carry audio, and says so.
 *
 * `connect` resolves rather than rejects, and then drives the listener to
 * `failed` with `'voice-engine-missing'`. That ordering is deliberate: the
 * provider's job is to reflect state, not to catch exceptions from a
 * dependency that was never installed, and a rejected promise here would look
 * to it exactly like a network failure on a real call.
 */
export const stubTransport = (): VoiceTransport => {
  const listeners = new Set<
    (state: VoiceTransportState, detail?: string) => void
  >();
  let state: VoiceTransportState = 'idle';

  const emit = (next: VoiceTransportState, detail?: string): void => {
    state = next;
    listeners.forEach(listener => listener(next, detail));
  };

  const missing = (method: string): void => {
    console.warn(
      `[voice] ${method}() called with no audio engine installed. ` +
        'The Synquic Voice client SDK has not been added to this app yet - ' +
        'see the header of src/voice/transport.ts.',
    );
  };

  return {
    isAvailable: false,

    async connect() {
      missing('connect');
      emit('connecting');
      /*
       * Failure on the next tick rather than in the same turn, so a subscriber
       * registered immediately after `connect()` still sees both transitions.
       * A synchronous jump to `failed` would be delivered to nobody.
       */
      setTimeout(() => emit('failed', 'voice-engine-missing'), 0);
    },

    async disconnect() {
      missing('disconnect');
      emit('closed');
    },

    async setMuted() {
      missing('setMuted');
    },

    async setSpeakerphone() {
      missing('setSpeakerphone');
    },

    subscribe(listener) {
      listeners.add(listener);
      // Replayed on subscribe, so a late subscriber is not stuck on 'idle'
      // waiting for a transition that has already happened.
      listener(state);
      return () => {
        listeners.delete(listener);
      };
    },
    subscribeProblems() {
      /* Nothing can go wrong on a transport that does nothing. The engine
         being missing is reported through `connect` as a failed state, which
         is a different and much louder thing than a call with no microphone. */
      return () => undefined;
    },
  };
};

/**
 * The real engine if it is installed, the stub if it is not.
 *
 * `require` in a `try` rather than a static `import` on purpose. A static one
 * is resolved by Metro at bundle time, so naming a package that is not in
 * `package.json` fails the build for everybody - including the people working
 * on parts of the app that have nothing to do with calling. This way the
 * absence is a runtime fact the UI can render.
 *
 * The eslint suppression is the same trade: this module genuinely is optional,
 * which is the one case the rule exists to catch.
 */
export const resolveTransport = (): VoiceTransport => {
  /*
   * The SDK shipped as a `SynquicVoiceCall` class rather than the
   * `createTransport()` factory this seam was written against, so the wrapping
   * lives in `sdk-adapter.ts`. It returns null both when the package is absent
   * and when it is present but this build has no WebRTC runtime - React Native
   * needs a native module the vendor supplies separately, and without it a
   * "connected" call would be silent.
   *
   * Either way the answer here is the stub, which fails loudly and lets the UI
   * say what is missing.
   */
  return resolveVendorTransport() ?? stubTransport();
};
